import { useEffect, useState, useCallback, createElement } from "react";
import { Row, Col } from "react-flexbox-grid";
import { PageWrapper } from "components";
import { fetchBackend, orderByAsc } from "helpers";
import { useSwipeable } from "react-swipeable";
import { useGlobalContext } from "context/GlobalContext";
import NotificationsIcon from "@material-ui/icons/Notifications";
import DeleteIcon from "@material-ui/icons/Delete";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DoneAllIcon from "@material-ui/icons/DoneAll";

import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Divider from "@material-ui/core/Divider";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import { useHistory } from "react-router-dom";
import { withLoader } from "HOC";
import styled from "styled-components";

const Notifications = ({ setLoading }) => {
  const [dataSource, setDataSource] = useState([]);
  const gContext = useGlobalContext();

  const getNotifications = useCallback(async (id) => {
    const { error, response } = await fetchBackend({
      url: `notification/user/${id}`,
    });

    if (!error) {
      orderByAsc(response, "dateCreated");
      setDataSource(response);
    } else {
      gContext.toggleAlert({
        message:
          "Hubo un error al intentar obtener los datos, revisa tu conexión a internet",
        type: "error",
      });
    }
  }, []);

  const getInitialState = useCallback(async () => {
    const id = gContext.currentUser?._id;
    if (id) {
      await Promise.all([getNotifications(id)]);
    }
    setLoading(false);
  }, [gContext.currentUser]);

  useEffect(() => {
    getInitialState();
  }, []);

  return (
    <PageWrapper
      isBack={false}
      header={
        <Row start="xs">
          <Col md={12}>
            <Row center="xs">
              <h1>Notificaciones</h1>
            </Row>
          </Col>
        </Row>
      }
    >
      <Col md={12}>
        <ListNotifications data={dataSource} />
      </Col>
    </PageWrapper>
  );
};

export default withLoader(Notifications, "Cargando notificaciones");

const ListNotifications = ({ data }) => {
  return (
    <List>
      {data.map(({ _id, title, description, dateCreated, isViewed, ...el }) => {
        return (
          <SingleNotification
            key={_id}
            _id={_id}
            title={title}
            isViewed={isViewed}
            description={description}
            dateCreated={dateCreated}
          />
        );
      })}
    </List>
  );
};

const StyledContentListItem = styled.div`
  display: flex;
`;

const StyledListItem = styled(ListItem)`
  transition: transform 1s ease;
  transform: ${(props) => {
    if (props.opened === "false") return "translateX(calc(-94px))";
    return "translateX(0%)";
  }};
`;

const StyledActionNotifications = styled.div`
  display: flex;
  width: 320px;
  align-items: center;
  cursor: pointer;

  .single-action {
    flex: 1 1 auto;
    display: flex;
    justify-content: center;
    height: 100%;
    margin: 2px;
  }

  .MuiSvgIcon-root {
    color: #fff;
  }

  .delete {
    background: #fd5555;
  }
  .view {
    background: #484848;
  }
  .done {
    background: #8ec54e;
  }

  display: ${(props) => props.display};
`;

const SingleNotification = ({
  _id,
  title,
  description,
  dateCreated,
  isViewed,
}) => {
  const [isOpen, setOpen] = useState(true);
  const gContext = useGlobalContext();
  const history = useHistory();

  const handlers = useSwipeable({
    onSwipedRight: () => setOpen(true),
    onSwipedLeft: () => setOpen(false),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const updateNotification = async () => {
    const { error } = await fetchBackend({
      url: `notification/${_id}`,
      method: "put",
      data: { isViewed: true },
    });

    if (!error) {
      gContext.toggleAlert({ message: "Notificación actualizada con exito" });
      setTimeout(() => window.location.reload(), 500);
    } else {
      gContext.toggleAlert({
        message:
          "Hubo un error al intentar obtener los datos, revisa tu conexión a internet",
        type: "error",
      });
    }
  };

  const deleteNotification = async () => {
    const { error } = await fetchBackend({
      url: `notification/${_id}`,
      method: "delete",
    });

    if (!error) {
      gContext.toggleAlert({ message: "Notificación eliminada con exito" });
      setTimeout(() => window.location.reload(), 500);
    } else {
      gContext.toggleAlert({
        message:
          "Hubo un error al intentar obtener los datos, revisa tu conexión a internet",
        type: "error",
      });
    }
  };

  const onAction = (type) => {
    switch (type) {
      case "done":
        updateNotification();
        break;
      case "delete":
        deleteNotification();
        break;

      default:
        history.push(`/notificaciones/${_id}`);
        break;
    }
  };

  return (
    <>
      <StyledContentListItem>
        <StyledListItem
          button
          alignItems="flex-start"
          opened={isOpen.toString()}
          {...handlers}
        >
          <ListItemAvatar>
            <Avatar>
              <NotificationsIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={title}
            secondary={
              <>
                <Typography
                  component="span"
                  variant="body2"
                  color="textPrimary"
                >
                  {description}
                </Typography>
                &nbsp;
                {new Date(dateCreated).toLocaleString()}
                &nbsp;
                <DoneAllIcon
                  style={{
                    color: isViewed ? "#00c200" : "#c2c2c2",
                    fontSize: 22,
                    marginBottom: "-4px",
                  }}
                />
              </>
            }
          />
        </StyledListItem>
        <StyledActionNotifications
          display={!isOpen ? "flex" : "none"}
          opened={isOpen.toString()}
        >
          <SingleAction
            title="Eliminar notificación"
            type="delete"
            icon={<DeleteIcon />}
            onClick={onAction}
          />
          <SingleAction
            title="Ver detalles"
            type="view"
            icon={<VisibilityIcon />}
            onClick={onAction}
          />
          <SingleAction
            title="Marcar como leída"
            type="done"
            icon={<DoneAllIcon />}
            onClick={onAction}
          />
        </StyledActionNotifications>
      </StyledContentListItem>
      <Divider variant="inset" component="li" />
    </>
  );
};

const SingleAction = ({ type, icon, onClick, title }) => {
  return (
    <div
      className={`single-action ${type}`}
      title={title}
      onClick={() => onClick(type)}
    >
      <Row middle="xs" center="xs">
        <Col xs={12}>{icon}</Col>
      </Row>
    </div>
  );
};
